import { ChevronDown } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  RANKING_FILTER_KEYS,
  RANKING_FILTER_LABELS,
  rankingFilterLabel,
  rankingFilterOptions,
  type RankingFilterKey,
  type RankingFilters,
  type RankingRow,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { SearchInput } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { useFocusOnOpen } from '../../../lib/use-focus-on-open';
import styles from './ranking.module.css';

// F23.11 (ADR 0049): RNK-01's filters. Each opens a pop-over by its button with a count per value; an active
// one shows what it holds ("Location Dhaka +1"), and the summary line lists each as a chip. They narrow the
// list only — no score, weight or tab moves.

/** Long lists (typed cities, sources) get a search box. */
const SEARCHABLE_FROM = 8;

interface RankingFilterBarProps {
  /** The rows the counts are taken from: the open tab, after keywords and the name search. */
  readonly rows: readonly RankingRow[];
  readonly filters: RankingFilters;
  readonly onChange: (filters: RankingFilters) => void;
  /** The filters this viewer is offered (Salary only to someone who may see salaries); all of them by default. */
  readonly keys?: readonly RankingFilterKey[];
  /** ADR 0068: more buttons in the same wrapping row (the profile lines' filters). */
  readonly extra?: ReactNode;
}

export function RankingFilterBar({ rows, filters, onChange, keys = RANKING_FILTER_KEYS, extra }: RankingFilterBarProps): ReactNode {
  const [open, setOpen] = useState<{ readonly key: RankingFilterKey; readonly anchor: AnchorRect } | null>(null);

  function set(key: RankingFilterKey, values: readonly string[]): void {
    onChange({ ...filters, [key]: values });
  }

  return (
    <>
      <div className={styles.filterRow} role="group" aria-label="Filters">
        {keys.map((key) => {
          const chosen = filters[key] ?? [];
          return (
            <button
              key={key}
              type="button"
              className={styles.filterButton}
              data-active={chosen.length > 0 || undefined}
              aria-haspopup="dialog"
              aria-expanded={open?.key === key}
              onClick={(event) => setOpen({ key, anchor: anchorFrom(event.currentTarget) })}
            >
              {RANKING_FILTER_LABELS[key]}
              {chosen[0] !== undefined && <span className={styles.filterValue}>{rankingFilterLabel(key, chosen[0])}</span>}
              {chosen.length > 1 && <span className={styles.filterMore}>+{chosen.length - 1}</span>}
              <ChevronDown size={14} aria-hidden />
            </button>
          );
        })}
        {extra}
      </div>
      <Popover anchor={open?.anchor ?? null} onClose={() => setOpen(null)} label={open ? RANKING_FILTER_LABELS[open.key] : 'Filter'} align="left">
        {open && <FilterPanel key={open.key} filterKey={open.key} rows={rows} filters={filters} onChange={(values) => set(open.key, values)} />}
      </Popover>
    </>
  );
}

interface FilterPanelProps {
  readonly filterKey: RankingFilterKey;
  readonly rows: readonly RankingRow[];
  readonly filters: RankingFilters;
  readonly onChange: (values: readonly string[]) => void;
}

function FilterPanel({ filterKey, rows, filters, onChange }: FilterPanelProps): ReactNode {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  const options = useMemo(() => rankingFilterOptions(rows, filters, filterKey), [rows, filters, filterKey]);
  const needle = query.trim().toLowerCase();
  const shown = needle === '' ? options : options.filter((option) => option.label.toLowerCase().includes(needle));
  const chosen = filters[filterKey] ?? [];
  useFocusOnOpen(searchRef, options.length >= SEARCHABLE_FROM);

  return (
    <div className={styles.filterPanel}>
      <span className={styles.filterPanelTitle}>{RANKING_FILTER_LABELS[filterKey]}</span>
      {options.length >= SEARCHABLE_FROM && (
        <SearchInput ref={searchRef} placeholder={`Search ${RANKING_FILTER_LABELS[filterKey].toLowerCase()}`} value={query} onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} aria-label={`Search ${RANKING_FILTER_LABELS[filterKey]}`} />
      )}
      {shown.length === 0 ? (
        <p className={styles.filterEmpty}>{options.length === 0 ? 'Nobody in this tab to filter.' : `Nothing matches "${query}".`}</p>
      ) : (
        <ul className={styles.filterOptions}>
          {shown.map((option) => (
            <li key={option.value} className={styles.filterOption}>
              <Checkbox
                checked={option.isSelected}
                label={option.label}
                onChange={(checked) => onChange(checked ? [...chosen, option.value] : chosen.filter((value) => value !== option.value))}
              />
              <span className={styles.filterOptionCount}>{option.count.toLocaleString('en-US')}</span>
            </li>
          ))}
        </ul>
      )}
      {chosen.length > 0 && (
        <Button size="sm" variant="ghost" onClick={() => onChange([])}>
          Clear {RANKING_FILTER_LABELS[filterKey].toLowerCase()}
        </Button>
      )}
    </div>
  );
}
